<script setup>
import { ref, reactive } from 'vue';
import { useI18n } from 'vue-i18n';
import Button from 'dashboard/components-next/button/Button.vue';
import PurchaseProjectItemBadge from './PurchaseProjectItemBadge.vue';
import { mockProducts } from './story/fixtures.js';

const emit = defineEmits(['update', 'remove']);

const { t } = useI18n();

const isOpen = ref(false);
const draft = reactive({
  productId: '',
  variantId: '',
  quantity: 1,
  sellerNote: '',
  isRecommended: false,
  sortOrder: 0,
});
const product = ref(null);
const originalItem = ref(null);

const formatCurrency = amount =>
  new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: product.value?.currency ?? 'EUR',
  }).format(amount);

const changeDraftQuantity = delta => {
  draft.quantity = Math.max(1, draft.quantity + delta);
};

const open = item => {
  originalItem.value = item;
  product.value = mockProducts.find(p => p.id === item.productId) ?? null;
  Object.assign(draft, { ...item });
  isOpen.value = true;
};

const close = () => {
  isOpen.value = false;
};

const handleSave = () => {
  emit('update', { ...draft });
  close();
};

const handleRemove = () => {
  emit('remove', { ...originalItem.value });
  close();
};

defineExpose({ open, close });
</script>

<template>
  <Teleport to="body">
    <div
      class="fixed inset-0 z-[70] flex items-end justify-center pointer-events-none transition-colors duration-200 ease-in-out"
      :class="
        isOpen
          ? 'pointer-events-auto bg-n-alpha-black1 backdrop-blur-[4px]'
          : 'bg-transparent'
      "
      @click.self="close"
    >
      <div
        class="w-full sm:max-w-xl max-h-[80vh] bg-n-solid-2 rounded-t-[1.5rem] sm:rounded-t-2xl shadow-xl flex flex-col overflow-hidden transition-transform duration-200 ease-in-out"
        :class="isOpen ? 'translate-y-0' : 'translate-y-full'"
      >
        <!-- Header -->
        <div class="flex items-center gap-3 px-4 py-3 shrink-0 bg-n-solid-1">
          <Button
            variant="ghost"
            color="slate"
            size="sm"
            icon="i-lucide-x"
            class="!rounded-full bg-n-alpha-black2 shrink-0"
            @click="close"
          />
          <h3 class="text-base font-medium text-n-slate-12 truncate">
            {{ t('PURCHASE_PROJECTS.ITEMS.EDIT_TITLE') }}
          </h3>
        </div>

        <!-- Scrollable content -->
        <div class="flex-1 overflow-y-auto overflow-x-hidden min-h-0">
          <div class="flex flex-col">
            <!-- Product info -->
            <div v-if="product" class="px-4 py-3 flex items-center gap-3">
              <img
                :src="product.image"
                :alt="product.title"
                class="size-14 shrink-0 rounded-lg object-cover bg-n-alpha-2"
              />
              <div class="flex flex-col flex-1 min-w-0 gap-0.5">
                <span class="text-sm font-medium text-n-slate-12 truncate">
                  {{ product.title }}
                </span>
                <span class="text-xs text-n-slate-10">{{ product.sku }}</span>
              </div>
              <div class="flex flex-col items-end shrink-0">
                <span class="text-sm font-semibold text-n-slate-12">
                  {{ formatCurrency(product.price) }}
                </span>
                <span
                  v-if="
                    product.compareAtPrice &&
                    product.compareAtPrice > product.price
                  "
                  class="text-xs text-n-slate-9 line-through"
                >
                  {{ formatCurrency(product.compareAtPrice) }}
                </span>
                <PurchaseProjectItemBadge :stock-status="product.stockStatus" />
              </div>
            </div>

            <!-- Variant -->
            <div class="px-4 py-3 flex flex-col gap-1.5">
              <label class="text-xs font-medium text-n-slate-10">
                {{ t('PURCHASE_PROJECTS.SEARCH.SELECT_VARIANT') }}
              </label>
              <div class="relative">
                <select
                  v-model="draft.variantId"
                  class="block w-full m-0 h-9 appearance-none bg-none border-0 rounded-lg bg-n-alpha-1 px-3 py-2 pr-8 text-sm text-n-slate-12 focus:outline-none"
                >
                  <option
                    v-for="variant in product?.variants ?? []"
                    :key="variant.id"
                    :value="variant.id"
                  >
                    {{ variant.label }}
                  </option>
                </select>
                <span
                  class="i-lucide-chevron-down size-3 text-n-slate-10 pointer-events-none absolute right-3 top-1/2 -translate-y-1/2"
                />
              </div>
            </div>

            <!-- Quantity -->
            <div class="px-4 py-3 flex flex-col gap-1.5">
              <span class="text-xs font-medium text-n-slate-10">
                {{ t('PURCHASE_PROJECTS.ITEMS.QUANTITY_LABEL') }}
              </span>
              <div
                class="flex items-center gap-3 rounded-lg border border-n-strong bg-n-alpha-1 px-1 self-start"
              >
                <button
                  type="button"
                  class="flex items-center justify-center size-8 text-n-slate-11 hover:text-n-slate-12 transition-colors"
                  @click="changeDraftQuantity(-1)"
                >
                  <span class="i-lucide-minus size-4" />
                </button>
                <span
                  class="min-w-[2rem] text-center text-sm font-semibold text-n-slate-12"
                >
                  {{ draft.quantity }}
                </span>
                <button
                  type="button"
                  class="flex items-center justify-center size-8 text-n-slate-11 hover:text-n-slate-12 transition-colors"
                  @click="changeDraftQuantity(1)"
                >
                  <span class="i-lucide-plus size-4" />
                </button>
              </div>
            </div>

            <!-- Recommended -->
            <div class="px-4 py-3 flex flex-col gap-1.5">
              <span class="text-xs font-medium text-n-slate-10">
                {{ t('PURCHASE_PROJECTS.ITEMS.RECOMMENDED') }}
              </span>
              <button
                type="button"
                class="flex items-center justify-between rounded-lg bg-n-alpha-1 px-3 py-2.5 transition-colors hover:bg-n-alpha-2"
                @click="draft.isRecommended = !draft.isRecommended"
              >
                <span class="text-sm text-n-slate-11">
                  {{ t('PURCHASE_PROJECTS.ITEMS.RECOMMENDED_HINT') }}
                </span>
                <span
                  class="i-lucide-star size-5 shrink-0 transition-colors"
                  :class="
                    draft.isRecommended
                      ? 'text-amber-500 fill-current'
                      : 'text-n-slate-10'
                  "
                />
              </button>
            </div>

            <!-- Seller note -->
            <div class="px-4 py-3 flex flex-col gap-1.5">
              <label class="text-xs font-medium text-n-slate-10">
                {{ t('PURCHASE_PROJECTS.ITEMS.SELLER_NOTE_LABEL') }}
              </label>
              <textarea
                v-model="draft.sellerNote"
                rows="3"
                class="w-full m-0 rounded-lg bg-n-alpha-1 px-3 py-2 text-sm text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none resize-none"
                :placeholder="
                  t('PURCHASE_PROJECTS.ITEMS.SELLER_NOTE_PLACEHOLDER')
                "
              />
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div
          class="px-4 pb-4 pt-3 bg-n-solid-1 border-t border-n-strong shrink-0 flex items-center gap-2"
        >
          <Button
            variant="ghost"
            color="ruby"
            size="md"
            icon="i-lucide-trash-2"
            @click="handleRemove"
          />
          <Button
            variant="outline"
            color="slate"
            size="md"
            class="flex-1"
            :label="t('PURCHASE_PROJECTS.ITEMS.CANCEL')"
            @click="close"
          />
          <Button
            variant="solid"
            color="blue"
            size="md"
            class="flex-1"
            :label="t('PURCHASE_PROJECTS.ITEMS.SAVE')"
            @click="handleSave"
          />
        </div>
      </div>
    </div>
  </Teleport>
</template>
